import { useState, type FormEvent } from 'react'
import { Eye, EyeOff, KeyRound, Loader2 } from 'lucide-react'
import { Panel } from '@/components/erp/primitives'
import { Note } from '@/shared/ui/states'
import { Field } from '@/shared/ui/Modal'
import { api } from '@/shared/api/client'
import { ApiError, explain } from '@/shared/api/problem'

const MIN_LENGTH = 12

/**
 * Change your own password, inline on the Overview tab.
 *
 * `POST /auth/password/change` needs the current password and a new one of at
 * least 12 characters — the same rule the backend enforces, checked here first so
 * the failure is immediate rather than a round trip.
 *
 * Changing the password revokes every refresh-token family, so this signs the
 * user out everywhere. Saying that before they commit is the whole reason this
 * has explanatory copy at all.
 */
export function ChangePasswordCard() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const mismatch = confirm.length > 0 && next !== confirm
  const tooShort = next.length > 0 && next.length < MIN_LENGTH
  const valid = current.length > 0 && next.length >= MIN_LENGTH && next === confirm

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!valid) return
    setBusy(true)
    setNote(null)
    setFieldErrors({})
    try {
      await api.post('/auth/password/change', { currentPassword: current, newPassword: next })
      setCurrent(''); setNext(''); setConfirm('')
      setNote({
        tone: 'good',
        text: 'Password changed. Other devices have been signed out.',
      })
    } catch (err) {
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors())
      setNote({ tone: 'bad', text: explain(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel
      title="Change your password"
      description={`At least ${MIN_LENGTH} characters. This signs you out of your other devices.`}
    >
      <form onSubmit={submit} className="space-y-3 max-w-md">
        {note && <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>}

        <Field label="Current password" required error={fieldErrors.currentPassword}>
          <input
            type={show ? 'text' : 'password'}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            className="erp-input w-full"
            required
          />
        </Field>

        <Field
          label="New password"
          required
          error={fieldErrors.newPassword ?? (tooShort ? `Needs at least ${MIN_LENGTH} characters.` : undefined)}
        >
          <div className="relative">
            <input
              type={show ? 'text' : 'password'}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              className="erp-input w-full pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              tabIndex={-1}
              aria-label={show ? 'Hide passwords' : 'Show passwords'}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground"
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </Field>

        <Field
          label="Confirm new password"
          required
          error={mismatch ? 'The two passwords do not match.' : undefined}
        >
          <input
            type={show ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className="erp-input w-full"
            required
          />
        </Field>

        <button type="submit" className="erp-btn erp-btn-primary" disabled={!valid || busy}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
          Change password
        </button>
      </form>
    </Panel>
  )
}
